import React from 'react';
import { Box, IconButton, Collapse } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

export interface FacetGroupProps {
  label: string;
  activeCount?: number;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

export const FacetGroup: React.FC<FacetGroupProps> = ({
  label,
  activeCount = 0,
  open,
  onToggle,
  children,
}) => {
  const hasActive = activeCount > 0;
  return (
    <Box
      sx={{
        py: 1.5,
        px: 2.25,
        borderTop: '1px solid',
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 'none' },
      }}
      data-testid={`facet-group-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          all: 'unset',
          width: '100%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          color: hasActive ? 'primary.main' : 'text.secondary',
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}
        aria-expanded={open}
      >
        {hasActive && (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
            }}
          />
        )}
        <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
          {label}
        </Box>
        {hasActive && (
          <Box
            component="span"
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: '999px',
              px: 0.75,
              py: 0.25,
              fontSize: '0.625rem',
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}
          >
            {activeCount}
          </Box>
        )}
        <IconButton
          size="small"
          sx={{
            p: 0,
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 225ms ease',
            color: 'text.secondary',
          }}
          disableRipple
          component="span"
        >
          <ExpandMore fontSize="small" />
        </IconButton>
      </Box>
      <Collapse in={open} timeout={225}>
        <Box sx={{ mt: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  );
};

export default FacetGroup;
